import { useState, useEffect, useCallback } from 'react';
import { 
  shouldBlockNewData, 
  shouldShowSyncWarning, 
  getSyncStatusSummary,
  SYNC_CONFIG 
} from '../utils/syncValidation';

const useSyncValidation = () => {
  const [syncWarning, setSyncWarning] = useState(null);
  const [syncBlock, setSyncBlock] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  // Check sync status
  const checkSyncStatus = useCallback(async () => {
    try {
      setIsChecking(true);

      // Check if data entry should be blocked
      const blockCheck = await shouldBlockNewData();
      if (blockCheck.blocked) {
        setSyncBlock(blockCheck);
        setSyncWarning(null); // Clear warning if blocking
        return;
      }

      // Check if warning should be shown
      const warningCheck = await shouldShowSyncWarning();
      if (warningCheck.showWarning) {
        setSyncWarning(warningCheck);
        setSyncBlock(null); // Clear block if only warning
      } else {
        // No issues
        setSyncWarning(null);
        setSyncBlock(null);
      }

      setLastCheck(new Date());
    } catch (error) {
      console.error('❌ Error checking sync status:', error);
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Check if user is logged in
  const isUserLoggedIn = () => {
    const token = localStorage.getItem("optha-token");
    const userData = localStorage.getItem("user_profile");
    return token && 
           token !== "" && 
           token !== "null" && 
           token !== "undefined" &&
           userData && 
           userData !== "null";
  };

  // Check if user is optometrist (only optometrists should see sync warnings)
  const isOptometrist = () => {
    const role = localStorage.getItem("optometrist_role");
    return role === "Optometrist";
  };

  // Check sync status on mount and when needed (only if logged in and is optometrist)
  useEffect(() => {
    if (isUserLoggedIn() && isOptometrist()) {
      checkSyncStatus();
    } else {
      // Clear warnings/blocks if user is logged out or is not optometrist
      setSyncWarning(null);
      setSyncBlock(null);
    }
  }, [checkSyncStatus]);

  // Auto-check every 3 hours (3 * 60 * 60 * 1000 = 10,800,000 milliseconds) - only if logged in and is optometrist
  useEffect(() => {
    if (!isUserLoggedIn() || !isOptometrist()) {
      return;
    }
    
    const interval = setInterval(() => {
      if (isUserLoggedIn() && isOptometrist()) {
        checkSyncStatus();
      } else {
        setSyncWarning(null);
        setSyncBlock(null);
      }
    }, 3 * 60 * 60 * 1000); // 3 hours

    return () => clearInterval(interval);
  }, [checkSyncStatus]);

  // Clear warnings/blocks
  const clearSyncWarning = useCallback(() => {
    setSyncWarning(null);
  }, []);

  const clearSyncBlock = useCallback(() => {
    setSyncBlock(null);
  }, []);

  // Force refresh sync status
  const refreshSyncStatus = useCallback(() => {
    checkSyncStatus();
  }, [checkSyncStatus]);

  // Check if new data can be added
  const canAddNewData = useCallback(() => {
    return !syncBlock?.blocked;
  }, [syncBlock]);

  // Get sync status summary
  const getSyncSummary = useCallback(async () => {
    try {
      return await getSyncStatusSummary();
    } catch (error) {
      console.error('❌ Error getting sync summary:', error);
      return {
        totalPending: 0,
        oldestPending: null,
        daysOld: null
      };
    }
  }, []);

  return {
    // State
    syncWarning,
    syncBlock,
    isChecking,
    lastCheck,
    
    // Actions
    checkSyncStatus,
    clearSyncWarning,
    clearSyncBlock,
    refreshSyncStatus,
    
    // Computed
    canAddNewData: canAddNewData(),
    
    // Utilities
    getSyncSummary,
    
    // Configuration
    config: SYNC_CONFIG
  };
};

export default useSyncValidation; 